import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { usePermissions } from "@/context/PermissionsContext";
import { useAIContext } from "@/context/AIContextProvider";
import { TID } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Leaf, Upload } from "lucide-react";
import { toast } from "sonner";

import { OverviewTab }      from "./tabs/OverviewTab";
import { EmissionsTab }     from "./tabs/EmissionsTab";
import { EnergyTab }        from "./tabs/EnergyTab";
import { WaterTab }         from "./tabs/WaterTab";
import { WasteTab }         from "./tabs/WasteTab";
import { HabitatTab }       from "./tabs/HabitatTab";
import { PeopleTab }        from "./tabs/PeopleTab";
import { AmpolTab }         from "./tabs/AmpolTab";
import { BillsInboxTab }    from "./tabs/BillsInboxTab";
import { FleetTab }         from "./tabs/FleetTab";
import { DriverLeagueTab }  from "./tabs/DriverLeagueTab";
import { AmpolImportDialog } from "./tabs/AmpolImportDialog";
import { ClientScope3Tab }  from "./tabs/ClientScope3Tab";

export default function SustainabilityView({ fy, buCode, bus }) {
  const { can, role } = usePermissions();
  const isClient = (role || "").toLowerCase() === "client";
  const { setContext } = useAIContext();
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [emTrend, setEmTrend] = useState([]);
  const [emissions, setEmissions] = useState([]);
  const [energy, setEnergy] = useState([]);
  const [water, setWater] = useState([]);
  const [waste, setWaste] = useState([]);
  const [habitat, setHabitat] = useState([]);
  const [people, setPeople] = useState([]);
  const [ampolReports, setAmpolReports] = useState([]);
  const [fleet, setFleet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const canCreate = can("hseq", "create");

  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams({ fy });
    if (buCode) p.set("business_unit_code", buCode);
    Promise.all([
      api.get(`/sustainability/overview?${p}`),
      api.get(`/sustainability/emissions/trend?${p}`),
      api.get(`/sustainability/emissions?${p}&limit=200`),
      api.get(`/sustainability/energy?${p}&limit=200`),
      api.get(`/sustainability/water?${p}&limit=200`),
      api.get(`/sustainability/waste?${p}&limit=200`),
      api.get(`/sustainability/habitat`),
      api.get(`/sustainability/people?${p}&limit=200`),
      api.get(`/sustainability/ampol/reports`).catch(() => ({ data: [] })),
      api.get(`/fleet/efficiency?fy=${fy}&limit=100`).catch(() => ({ data: null })),
    ])
      .then(([o, t, em, en, wa, ws, hb, pp, ar, fl]) => {
        setOverview(o.data); setEmTrend(t.data.trend || []);
        setEmissions(em.data); setEnergy(en.data); setWater(wa.data); setWaste(ws.data);
        setHabitat(hb.data); setPeople(pp.data); setAmpolReports(ar.data);
        setFleet(fl.data);
      })
      .catch((e) => {
        console.warn("[Sustainability] load failed", e?.message || e);
        toast.error("Failed to load sustainability data");
      })
      .finally(() => setLoading(false));
  }, [fy, buCode]);

  useEffect(() => {
    if (!overview) return;
    setContext({
      module: "hseq",
      section: "sustainability",
      fy,
      business_unit: buCode || "All BUs",
      total_co2e_kg: overview.emissions.total_co2e_kg,
      scope_1_kg: overview.emissions.scope_1_kg,
      scope_2_kg: overview.emissions.scope_2_kg,
      scope_3_kg: overview.emissions.scope_3_kg,
      fuel_litres: overview.emissions.fuel_litres,
      electricity_kwh: overview.energy.electricity_kwh,
      solar_generated_kwh: overview.energy.solar_generated_kwh,
      water_recycled_pct: overview.water.recycled_pct,
      waste_diverted_pct: overview.waste.diverted_pct,
      hectares_managed: overview.habitat.hectares_managed,
      community_hours: overview.people.community_hours,
    });
    return () => setContext(null);
    /* eslint-disable-next-line */
  }, [overview, fy, buCode]);

  if (loading || !overview) {
    return <div className="h-64 rounded-lg bg-white border border-slate-200 shimmer" />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-emerald-900">
          <Leaf className="w-4 h-4" />
          <span className="font-medium">Sustainability Site</span>
          <span className="text-xs text-slate-500">
            · Emissions · Energy · Water · Waste · Habitat &amp; Biodiversity · People
          </span>
        </div>
        {canCreate && (
          <div className="flex items-center gap-2">
          <Button
            data-testid="sust-rollforward-btn"
            onClick={async () => {
              try {
                const { data } = await api.post(`/sustainability/emissions/rollforward?fy=${fy}`);
                toast.success(`${fy} scope 2 roll-forward: ${data.filled} month(s) estimated from last year, ${data.skipped_actuals} already have actuals`);
              } catch (e) { toast.error(e.response?.data?.detail || "Roll-forward failed"); }
            }}
            size="sm"
            variant="outline"
            className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
          >
            Roll forward scope 2 ({fy})
          </Button>
          <Button
            data-testid={TID.sustAmpolImportOpen}
            onClick={() => setImportOpen(true)}
            size="sm"
            className="bg-emerald-800 hover:bg-emerald-900 text-white"
          >
            <Upload className="w-3.5 h-3.5 mr-1.5" /> Import Ampol Report
          </Button>
          </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-50 border border-slate-200 p-1 h-auto">
          <TabsTrigger data-testid={TID.sustTab("overview")}  value="overview"  className="text-xs data-[state=active]:bg-white">Overview</TabsTrigger>
          <TabsTrigger data-testid={TID.sustTab("emissions")} value="emissions" className="text-xs data-[state=active]:bg-white">Emissions</TabsTrigger>
          <TabsTrigger data-testid={TID.sustTab("energy")}    value="energy"    className="text-xs data-[state=active]:bg-white">Energy</TabsTrigger>
          <TabsTrigger data-testid={TID.sustTab("water")}     value="water"     className="text-xs data-[state=active]:bg-white">Water</TabsTrigger>
          <TabsTrigger data-testid={TID.sustTab("waste")}     value="waste"     className="text-xs data-[state=active]:bg-white">Waste</TabsTrigger>
          <TabsTrigger data-testid={TID.sustTab("habitat")}   value="habitat"   className="text-xs data-[state=active]:bg-white">Habitat</TabsTrigger>
          <TabsTrigger data-testid={TID.sustTab("people")}    value="people"    className="text-xs data-[state=active]:bg-white">People</TabsTrigger>
          <TabsTrigger data-testid="sust-tab-scope3"          value="scope3"    className="text-xs data-[state=active]:bg-emerald-700 data-[state=active]:text-white">Client Scope 3</TabsTrigger>
          {!isClient && (
            <>
              <TabsTrigger data-testid={TID.sustTab("bills")}     value="bills"     className="text-xs data-[state=active]:bg-white">Bills Inbox</TabsTrigger>
              <TabsTrigger data-testid={TID.sustTab("ampol")}     value="ampol"     className="text-xs data-[state=active]:bg-white">Ampol</TabsTrigger>
              <TabsTrigger data-testid={TID.sustTab("fleet")}     value="fleet"     className="text-xs data-[state=active]:bg-white">Fleet Efficiency</TabsTrigger>
              <TabsTrigger data-testid={TID.sustTab("drivers")}   value="drivers"   className="text-xs data-[state=active]:bg-white">Driver League</TabsTrigger>
            </>
          )}
        </TabsList>

        <TabsContent value="scope3" className="mt-4">
          <ClientScope3Tab fy={fy} />
        </TabsContent>

        <TabsContent value="overview" className="mt-4">
          <OverviewTab overview={overview} emTrend={emTrend} />
        </TabsContent>
        <TabsContent value="emissions" className="mt-4">
          <EmissionsTab overview={overview} trend={emTrend} rows={emissions} />
        </TabsContent>
        <TabsContent value="energy" className="mt-4">
          <EnergyTab overview={overview} rows={energy} />
        </TabsContent>
        <TabsContent value="water" className="mt-4">
          <WaterTab overview={overview} rows={water} />
        </TabsContent>
        <TabsContent value="waste" className="mt-4">
          <WasteTab overview={overview} rows={waste} />
        </TabsContent>
        <TabsContent value="habitat" className="mt-4">
          <HabitatTab rows={habitat} overview={overview} />
        </TabsContent>
        <TabsContent value="people" className="mt-4">
          <PeopleTab overview={overview} rows={people} />
        </TabsContent>
        <TabsContent value="bills" className="mt-4">
          <BillsInboxTab canCreate={canCreate} />
        </TabsContent>
        <TabsContent value="ampol" className="mt-4">
          <AmpolTab reports={ampolReports} onOpenImport={() => setImportOpen(true)} canImport={canCreate} />
        </TabsContent>
        <TabsContent value="fleet" className="mt-4">
          <FleetTab fleet={fleet} />
        </TabsContent>
        <TabsContent value="drivers" className="mt-4">
          <DriverLeagueTab reports={ampolReports} />
        </TabsContent>
      </Tabs>

      <AmpolImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={() => {
          setImportOpen(false);
          setTab((t) => t);
          const p = new URLSearchParams({ fy });
          if (buCode) p.set("business_unit_code", buCode);
          Promise.all([
            api.get(`/sustainability/overview?${p}`),
            api.get(`/sustainability/emissions/trend?${p}`),
            api.get(`/sustainability/ampol/reports`),
          ]).then(([o, t, r]) => {
            setOverview(o.data); setEmTrend(t.data.trend || []);
            setAmpolReports(r.data);
          });
        }}
      />
    </div>
  );
}
